import { useCallback, useContext, useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import { markRovoAutoOpened } from './useOpenRovoOnEditPageReady.ts';

const AGENT_ONBOARDING_SOURCE = 'confluence-rovo-agent-onboarding';
const AGENTS_SPOTLIGHTS_TRIGGER_ID = 'confluence-agents-spotlights';
const AUTO_OPEN_DELAY_MS = 500;

const normalizeSingleQueryParam = (queryParam: string | string[] | undefined) =>
	typeof queryParam === 'string' ? queryParam : undefined;

type UseOpenSelectedAgentOnEditPageReadyArgs = {
	contentId: string;
	isLivePage: boolean;
	isLivePageReady: boolean;
	isRovoBridgeReady: boolean;
};

/**
 * A selected agent is carried through page creation as `rovoAgentId`, consumed once after the
 * destination editor and its Rovo content bridge are ready, and removed from the canonical URL.
 * Waiting for the bridge means the assistant has registered the final page as its current context
 * before the sidebar and spotlight open. This takes precedence over the generic mini-modal
 * auto-open path.
 */
export const useOpenSelectedAgentOnEditPageReady = ({
	contentId,
	isLivePage,
	isLivePageReady,
	isRovoBridgeReady,
}: UseOpenSelectedAgentOnEditPageReadyArgs): void => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { getQueryParams } = useContext(RoutesContext);
	const { setQueryParams } = useRouteActions();
	const publish = usePublish('ai-mate');
	const { fireTrigger: fireAgentsSpotlightsTrigger } = useFirePostOfficeTrigger({
		triggerId: AGENTS_SPOTLIGHTS_TRIGGER_ID,
	});

	// Capture the creation and agent-onboarding query params at mount. Refs keep both triggers
	// stable when the onboarding parameter is removed from the canonical URL after consumption.
	const { draftShareId, rovoAgentId } = getQueryParams();
	const draftShareIdRef = useRef(normalizeSingleQueryParam(draftShareId));
	const rovoAgentIdRef = useRef(normalizeSingleQueryParam(rovoAgentId));
	const isEditPageReadyRef = useRef(false);
	const isLivePageReadyRef = useRef(isLivePageReady);
	const isRovoBridgeReadyRef = useRef(isRovoBridgeReady);
	const hasOpenedRef = useRef(false);
	const openTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const tryOpenSelectedAgent = useCallback(() => {
		const agentId = rovoAgentIdRef.current;
		const isDestinationEditorReady =
			(!!draftShareIdRef.current && isEditPageReadyRef.current) ||
			(isLivePage && isLivePageReadyRef.current);

		if (
			!agentId ||
			hasOpenedRef.current ||
			!isDestinationEditorReady ||
			!isRovoBridgeReadyRef.current ||
			openTimerRef.current !== undefined
		) {
			return;
		}

		// One-shot buffer: the first eligible trigger arms a one-time timer so the Rovo surface has time
		// to mount/register before we open it (avoids a mount-order race). While a timer is already
		// pending, subsequent triggers are no-ops — we never clear or reschedule, so rapid repeated
		// triggers cannot starve the open. The one-shot is consumed only when the timer actually fires.
		openTimerRef.current = setTimeout(() => {
			openTimerRef.current = undefined;
			hasOpenedRef.current = true;
			// Persist that we've auto-opened for this content so we don't re-open if the user dismisses
			// and navigates back to the same draft/live doc. Remembered for 14 days (see
			// ROVO_AUTO_OPENED_TTL_SECONDS); after that the mini-modal may auto-open again.
			markRovoAutoOpened(contentId);
			publish({
				type: 'chat-new',
				source: AGENT_ONBOARDING_SOURCE,
				openChatMode: 'sidebar',
				data: {
					dialogues: [],
					resetActiveMenu: true,
					skipCreatingSeededConversation: true,
					agentId,
				},
			});
			fireAgentsSpotlightsTrigger({ isActive: true });
			setQueryParams({ rovoAgentId: null }, true);
		}, AUTO_OPEN_DELAY_MS);
	}, [contentId, fireAgentsSpotlightsTrigger, isLivePage, publish, setQueryParams]);

	useEffect(() => {
		isLivePageReadyRef.current = isLivePageReady;
		tryOpenSelectedAgent();
	}, [isLivePageReady, tryOpenSelectedAgent]);

	useEffect(() => {
		isRovoBridgeReadyRef.current = isRovoBridgeReady;
		tryOpenSelectedAgent();
	}, [isRovoBridgeReady, tryOpenSelectedAgent]);

	useEffect(() => {
		if (!draftShareIdRef.current || !rovoAgentIdRef.current) {
			return;
		}

		// Draft/new pages open after edit-page/load succeeds; live docs use collab readiness instead.
		return experienceTracker.subscribe((event) => {
			if (event.name === EDIT_PAGE_LOAD_EXPERIENCE && event.action === 'taskSuccess') {
				isEditPageReadyRef.current = true;
				tryOpenSelectedAgent();
			}
		});
	}, [experienceTracker, tryOpenSelectedAgent]);

	useEffect(
		() => () => {
			if (openTimerRef.current !== undefined) {
				clearTimeout(openTimerRef.current);
			}
		},
		[],
	);
};
